import React, { useEffect, useState } from 'react';
import { Sparkles, ArrowRight, ShieldCheck, Star, CircleCheck } from 'lucide-react';

function useCountUp(target, duration = 2000, isPercent = false, isMoney = false) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const animate = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setV(target * eased);
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  if (isPercent) return v.toFixed(1);
  if (isMoney) return v.toFixed(2);
  return Math.round(v).toLocaleString();
}

// Character-by-character split for hero heading.
// Preserves word boundaries (each <span class="hero-word"> wraps a word; characters animate inside).
function SplitHeading({ text, baseDelay = 0 }) {
  const words = text.split(' ');
  let charIndex = 0;
  return (
    <>
      {words.map((word, wi) => (
        <span key={wi} className="hero-word">
          {Array.from(word).map((ch, ci) => {
            const i = charIndex++;
            return (
              <span
                key={ci}
                className="hero-char"
                style={{ animationDelay: `${baseDelay + i * 30}ms` }}
              >
                {ch}
              </span>
            );
          })}
          {wi < words.length - 1 && <span className="hero-char hero-space">&nbsp;</span>}
        </span>
      ))}
    </>
  );
}

export default function Hero() {
  const meetings = useCountUp(312);
  const reply = useCountUp(11.4, 2000, true);
  const pipeline = useCountUp(1.82, 2000, false, true);
  const campaigns = useCountUp(14);

  return (
    <section className="hero" id="main">
      <div className="hero-aurora" aria-hidden="true" />
      <div className="grid-bg" aria-hidden="true" />
      <div className="container-x" style={{ textAlign: 'center' }}>
        <span className="eyebrow hero-anim-in" style={{ animationDelay: '0ms' }}>
          <Sparkles size={14} /> For B2B Agencies · SaaS · Consultants
        </span>
        <h1 className="hero-headline">
          <span style={{ color: '#ffffff' }}>Book qualified meetings with </span>
          <span
            style={{
              background: 'linear-gradient(90deg, #3b8bff, #60a5fa, #3b8bff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Ideal Client Personas through an allbound system
          </span>
          <span style={{ color: '#ffffff' }}> of LinkedIn and emails.</span>
        </h1>
        <p className="hero-sub hero-anim-in" style={{ animationDelay: '240ms' }}>
          We help <span style={{ color: '#fff', fontWeight: 500 }}>B2B agencies, SaaS companies, and consultants</span> book qualified, sales-ready meetings on autopilot, using an AI outbound system built, launched, and optimized in 14 days.
        </p>
        <div className="hero-anim-in" style={{ marginTop: 40, animationDelay: '360ms' }}>
          <a className="btn-primary" href="https://cal.com/one-up-n0typj/discovery" target="_blank" rel="noopener noreferrer">
            <span className="btn-label">Book A Call <ArrowRight size={16}/></span>
          </a>
        </div>
        <div className="hero-trust hero-anim-in" style={{ animationDelay: '480ms' }}>
          <ShieldCheck size={15} />Join 50+ agencies · results in 14 days
        </div>
        <div className="hero-stats hero-anim-in" style={{ animationDelay: '600ms' }}>
          <div className="hero-stat">
            <div style={{ display: 'flex' }}>
              {[0,1,2,3,4].map(i => <Star key={i} size={14} className="star-cyan"/>)}
            </div>
            <span style={{ marginLeft: 6 }}>4.8 from 20+ B2B founders</span>
          </div>
          <span className="dot-sep" />
          <div className="hero-stat"><CircleCheck size={14} style={{ color: 'var(--accent-bright)' }}/> <span>312+ meetings booked</span></div>
          <span className="dot-sep" />
          <div className="hero-stat"><CircleCheck size={14} style={{ color: 'var(--accent-bright)' }}/> <span>$4.8M+ in generated opportunities</span></div>
        </div>

        <div className="dashboard-wrap hero-anim-in" style={{ animationDelay: '720ms' }}>
          <div className="dashboard-glow" />
          <div className="dashboard glass-card">
            <div className="card-shimmer-line" aria-hidden="true" />
            <div className="dashboard-head">
              <div className="window-dots"><span/><span/><span/></div>
              <div className="window-title">pipeline.justicp, live</div>
              <span className="live-badge">● Active</span>
            </div>
            <div className="metrics-grid">
              <div className="metric">
                <div className="metric-label">Meetings booked</div>
                <div className="metric-row"><div className="metric-value">{meetings}</div><span className="metric-delta up">+28%</span></div>
              </div>
              <div className="metric">
                <div className="metric-label">Reply rate</div>
                <div className="metric-row"><div className="metric-value">{reply}%</div><span className="metric-delta cyan">+3.2pt</span></div>
              </div>
              <div className="metric">
                <div className="metric-label">Pipeline value</div>
                <div className="metric-row"><div className="metric-value">${pipeline}M</div><span className="metric-delta up">+42%</span></div>
              </div>
              <div className="metric">
                <div className="metric-label">Active campaigns</div>
                <div className="metric-row"><div className="metric-value">{campaigns}</div><span className="metric-delta cyan">+6</span></div>
              </div>
            </div>
            <div className="bar-chart-wrap">
              <div className="bar-chart-head">
                <div>
                  <div className="bar-chart-eyebrow">Last 12 weeks</div>
                  <div className="bar-chart-title">Qualified meetings booked</div>
                </div>
              </div>
              <div className="bars">
                {[14,22,18,30,26,38,32,46,40,58,52,72].map((h, i) => (
                  <div key={i} className="bar" style={{ height: `${h}%`, animationDelay: `${0.8 + i * 0.06}s` }} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
